import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Card,
  CardContent,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Download as DownloadIcon,
  PendingActions as PendingIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { downloadSignedFormCopy } from '../../../utils/signedFormDownload';

const FormIndex = ({ onFormSelect }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { i18n } = useTranslation();
  // Normalize language code: 'es-MX' -> 'es', 'en' -> 'en'
  const language = (i18n.language || 'en').startsWith('es') ? 'es' : 'en';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    user: null,
    forms: []
  });

  const t = {
    en: {
      title: 'Sign-Off Sheets',
      subtitle: 'Welcome to the employee portal. Here you will find all the sign-off sheets that must be completed prior to your employment.',
      availableForms: 'Available Forms',
      pleaseComplete: 'Please complete the following forms',
      formName: 'Form Name',
      status: 'Status',
      english: 'English',
      spanish: 'Spanish',
      completed: 'Completed',
      pending: 'Pending',
      completeForm: 'Complete Form',
      alreadySubmitted: 'Already Submitted',
      signedCopy: 'Signed Copy',
      download: 'Download',
      loading: 'Loading forms...'
    },
    es: {
      title: 'Formularios de Empleo',
      subtitle: 'Bienvenido al portal de empleados. Aquí encontrará todos los formularios que debe completar antes de su empleo.',
      availableForms: 'Formularios Disponibles',
      pleaseComplete: 'Por favor complete los siguientes formularios',
      formName: 'Nombre del Formulario',
      status: 'Estado',
      english: 'Inglés',
      spanish: 'Español',
      completed: 'Completado',
      pending: 'Pendiente',
      completeForm: 'Completar Formulario',
      alreadySubmitted: 'Ya Enviado',
      signedCopy: 'Copia Firmada',
      download: 'Descargar',
      loading: 'Cargando formularios...'
    }
  };

  const labels = t[language] || t.en;

  useEffect(() => {
    fetchUserForms();
  }, []);

  const fetchUserForms = async () => {
    try {
      setLoading(true);
      const response = await makeApiCall('/api/employee-forms/my-forms');
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load forms');
      }
      
      setFormData(data);
      setError('');
    } catch (err) {
      console.error('Error fetching forms:', err);
      setError(err.message || 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const handleFormClick = (formId, overrideLanguage = null) => {
    // Use override language if provided (from English/Spanish buttons)
    // Otherwise use current global language
    const langToUse = overrideLanguage || language;
    
    // If onFormSelect callback is provided (Dashboard mode), use it
    // Otherwise use React Router navigation (standalone mode)
    if (onFormSelect) {
      onFormSelect(formId, langToUse);
    } else {
      navigate(`/forms/${formId}?lang=${langToUse}`);
    }
  };

  // Legacy client-side HTML/print fallback (TKT-199) — used only if the
  // server-side PDF (TKT-249) is unavailable.
  const downloadSignedCopyHtmlFallback = async (form) => {
    const response = await makeApiCall(`/api/employee-forms/${form.submission_id}`);
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to load signed form');
    }
    const submission = data.form;
    downloadSignedFormCopy({
      formTitle: language === 'es' ? submission.form_name_es : submission.form_name_en,
      employeeName: submission.employee_name || `${submission.first_name || ''} ${submission.last_name || ''}`.trim(),
      submittedAt: submission.submitted_at,
      language: submission.language || language,
      formData: typeof submission.form_data === 'string' ? JSON.parse(submission.form_data) : submission.form_data,
      signatureData: submission.signature_data
    });
  };

  const handleDownloadSignedCopy = async (form) => {
    if (!form.submission_id) return;
    setError('');
    try {
      // TKT-249: prefer the real server-side PDF — reliable on desktop and
      // iPad Safari, where the old window.print() flow failed.
      const pdfResponse = await makeApiCall(`/api/employee-forms/${form.submission_id}/pdf`);
      if (!pdfResponse.ok) {
        throw new Error('PDF generation failed');
      }
      const blob = await pdfResponse.blob();
      const title = (language === 'es' ? form.form_name_es : form.form_name_en) || 'signed-form';
      const safeTitle = title.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'signed-form';
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeTitle}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (pdfErr) {
      console.warn('Server-side PDF failed, falling back to HTML copy:', pdfErr);
      try {
        await downloadSignedCopyHtmlFallback(form);
      } catch (err) {
        console.error('Error downloading signed copy:', err);
        setError(err.message || 'Failed to download signed form');
      }
    }
  };

  const getCompletedCount = () => {
    return formData.forms?.filter(f => f.status === 'completed').length || 0;
  };

  const getPendingCount = () => {
    return formData.forms?.filter(f => f.status === 'pending').length || 0;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          {labels.loading}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }} data-demo-target="page-forms-root">
      {/* Header Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {labels.title}
        </Typography>
        <Typography variant="body2">
          {labels.subtitle}
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* User Profile Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    mr: 2,
                    bgcolor: 'primary.main'
                  }}
                >
                  <PersonIcon fontSize="large" />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {formData.user?.first_name} {formData.user?.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formData.user?.username}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">
                    {labels.completed}:
                  </Typography>
                  <Chip
                    label={getCompletedCount()}
                    size="small"
                    color="success"
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">
                    {labels.pending}:
                  </Typography>
                  <Chip
                    label={getPendingCount()}
                    size="small"
                    color="warning"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Forms Table */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {labels.availableForms}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {labels.pleaseComplete}
              </Typography>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{labels.formName}</TableCell>
                      <TableCell align="center">{labels.status}</TableCell>
                      <TableCell align="center">{labels.signedCopy}</TableCell>
                      <TableCell align="center">{labels.english}</TableCell>
                      <TableCell align="center">{labels.spanish}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.forms?.map((form) => (
                      <TableRow key={form.form_id}>
                        <TableCell>
                          <Typography variant="body2">
                            {language === 'es' ? form.form_name_es : form.form_name_en}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {form.status === 'completed' && form.submission_id ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={() => handleDownloadSignedCopy(form)}
                              aria-label={`${labels.download} ${language === 'es' ? form.form_name_es : form.form_name_en}`}
                            >
                              {labels.download}
                            </Button>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {form.status === 'completed' ? (
                            <Chip
                              icon={<CheckIcon />}
                              label={labels.completed}
                              color="success"
                              size="small"
                            />
                          ) : (
                            <Chip
                              icon={<PendingIcon />}
                              label={labels.pending}
                              color="warning"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {form.status === 'completed' ? (
                            <Typography variant="caption" color="text.secondary">
                              {labels.alreadySubmitted}
                            </Typography>
                          ) : (
                            <>
                              {/* Show both language buttons if needed, or just one based on current language */}
                              <Button
                                size="small"
                                variant={language === 'en' ? 'contained' : 'outlined'}
                                onClick={() => handleFormClick(form.form_id, 'en')}
                              >
                                {labels.completeForm}
                              </Button>
                            </>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {form.status === 'completed' ? (
                            <Typography variant="caption" color="text.secondary">
                              {labels.alreadySubmitted}
                            </Typography>
                          ) : (
                            <Button
                              size="small"
                              variant={language === 'es' ? 'contained' : 'outlined'}
                              onClick={() => handleFormClick(form.form_id, 'es')}
                            >
                              {labels.completeForm}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default FormIndex;

